import {
  BaseOrganizationInfo,
  OrganizationInfo,
  OrganizationLineage,
  OrganizationMember,
  ParentOrganizationInfo,
} from ".";
import * as api from "./api";
import { useParams } from "react-router-dom";
import { Idp } from "services/security";

export const settings = {
  maxOrganizationNestingLevels: 4,
};

// Global state whose state doesn't relate to any rendering (thus not in React.Context or other state mechanism).
const privateStore: { idps?: Idp[] } = {};

const useCOPOrganizationService = () => {
  const params = useParams() as { organizationId?: string };

  function getCurrentOrganizationId() {
    return params.organizationId;
  }

  function addOrUpdateOrganization(isNew: boolean, organization: OrganizationInfo | BaseOrganizationInfo) {
    return api.saveOrganization(isNew, organization);
  }

  function addOrganizationMembers(organizationId: string, emails: string[]) {
    return api.addOrganizationMembers(organizationId, emails);
  }

  function updateOrganizationMemberRoles(organizationId: string, organizationMember: OrganizationMember) {
    return api.updateOrganizationMemberRoles(organizationId, organizationMember);
  }

  function deleteOrganizationMember(organizationId: string, organizationMember: OrganizationMember) {
    return api.deleteOrganizationMember(organizationId, organizationMember.user.id);
  }

  function deleteOrganization(organizationId: string) {
    return api.deleteOrganization(organizationId);
  }

  function getOrganizationDetails(id: string) {
    return api.getOrganizationDetails(id);
  }

  function getOrganizationLoginConfiguration(id: string) {
    return api.getOrganizationLoginConfiguration(id);
  }

  function generateNewOrganization(): BaseOrganizationInfo {
    return {
      name: "",
      accessType: "EXTERNAL",
      contactEmail: null,
    };
  }

  function canAddNestedOrganizations(organization: OrganizationLineage) {
    const nestingLevel = getOrgLevel(organization);
    return nestingLevel < settings.maxOrganizationNestingLevels;
  }

  function getOrgLevel(organization: OrganizationLineage) {
    if (!organization.parent) {
      return 0;
    } else {
      let parent: ParentOrganizationInfo | undefined = organization.parent;
      let orgLevelCount = 1;
      while (parent) {
        orgLevelCount++;

        parent = parent.parent;
      }
      return orgLevelCount;
    }
  }

  function revokeInvitation(organizationId: string, invitationId: number) {
    return api.revokeInvitation(organizationId, invitationId);
  }

  function getAllIdps(): Idp[] {
    return privateStore.idps!;
  }

  async function ensureAuthorizationStoreIsInitialized() {
    if (!privateStore.idps) {
      const idps = await api.getAllIdps();
      privateStore.idps = idps;
    }
    return privateStore.idps;
  }

  return {
    addOrUpdateOrganization,
    addOrganizationMembers,
    canAddNestedOrganizations,
    deleteOrganization,
    deleteOrganizationMember,
    generateNewOrganization,
    getCurrentOrganizationId,
    getOrganizationDetails,
    getOrganizationLineage: api.getOrganizationHierarchy,
    getTopLevelOrganizations: api.getTopLevelOrganizations,
    updateOrganizationMemberRoles,
    revokeInvitation,
    getAllIdps,
    getOrganizationLoginConfiguration,
    ensureAuthorizationStoreIsInitialized,
  };
};

export default useCOPOrganizationService;
